import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/user';
import { useCart } from '../contexts/cart';
import api from '../api/api';
import log from '../utils/logger';
import { getApiErrorMessage } from '../utils/apiError';

const useCartActions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, idToken, logout, adminImpersonate } = useUser();
  const { cartItems, setCart, updateQuantity, clearCart, setCartId } = useCart();
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('info');

  const showToast = useCallback((msg, type = 'info') => {
    setToastMessage(msg);
    setToastType(type);
    setTimeout(() => {
      setToastMessage('');
      setToastType('info');
    }, 3000);
  }, []);

  const handleSessionExpired = useCallback(() => {
    sessionStorage.setItem('sessionExpired', 'true');
    sessionStorage.setItem(
      'toast',
      JSON.stringify({
        message: t('sessionExpiredMessage'),
        type: 'error',
      }),
    );
    navigate('/home');
    logout();
    clearCart();
  }, [showToast, t, navigate, logout, clearCart]);

  const handleCartError = useCallback((error) => {
    const rawMessage = typeof error?.response?.data?.error === 'string'
      ? error.response.data.error
      : '';
    if (rawMessage === 'TOKEN_EXPIRED' || rawMessage === 'INVALID_ID_TOKEN') {
      handleSessionExpired();
    } else if (rawMessage.toLowerCase().includes('missing email')) {
      showToast(t('loginRequiredMessage'), 'warning');
    } else {
      showToast(getApiErrorMessage(error, t), 'error');
    }
  }, [t, handleSessionExpired, showToast]);

  const addToCart = useCallback(async (product, { quantity, variantId, onSuccess } = {}) => {
    const qty = quantity ?? product.minQuantity ?? 1;

    if (!user) {
      showToast(t('loginRequiredMessage'), 'warning');
      return;
    }

    try {
      const response = await api.post(
        '/buyer/cart/createOrUpdateCart',
        {
          productId: product._id,
          variantId: variantId ?? undefined,
          quantity: qty,
          merchantConfigId: product.merchantConfigId,
          adminImpersonateToken: adminImpersonate || null,
          buyerEmail: user?.email,
          buyerId: user?.id,
        },
        { headers: { Authorization: `Bearer ${idToken}` } },
      );

      const { cartId, cartItems: items } = response.data.response.data;
      setCart(items || []);
      setCartId(cartId || null);
      onSuccess?.();
    } catch (error) {
      handleCartError(error);
    }
  }, [adminImpersonate, user, idToken, setCart, setCartId, handleCartError]);

  const getQuantityFromCart = useCallback((productId, variantId = null) => {
    const item = cartItems.find(item =>
      item.productId === productId &&
      (variantId ? item.variantId === variantId : true),
    );
    return item?.quantity || 0;
  }, [cartItems]);

  const getMaxVariantFromCart = useCallback((productId) => {
    const variants = cartItems.filter(item => item.productId === productId);
    if (!variants.length) {
      return { quantity: 0, item: null };
    }
    const maxItem = variants.reduce((max, current) =>
      current.quantity > max.quantity ? current : max,
    );
    return { quantity: maxItem.quantity, item: maxItem };
  }, [cartItems]);

  const getVariantQuantitiesFromCart = useCallback((productId) => {
    const items = cartItems.filter(
      (item) => item.productId === productId,
    );

    if (!items.length) {
      return {};
    }

    return items.reduce((acc, item) => {
      acc[item.variantId || 'default'] = item.quantity;
      return acc;
    }, {});
  }, [cartItems]);

  const updateQuantityOnServer = useCallback(async (product, quantity) => {
    try {
      const response = await api.post(
        '/buyer/cart/createOrUpdateCart',
        {
          productId: product._id,
          variantId: product?.variants?.length !== 0 && product?.variantId ? product.variantId : undefined,
          quantity,
          merchantConfigId: product.merchantConfigId,
          adminImpersonateToken: adminImpersonate || null,
          buyerEmail: user?.email,
          buyerId: user?.id,
        },
        { headers: { Authorization: `Bearer ${idToken}` } },
      );

      const { cartId } = response.data.response.data;
      setCartId(cartId || null);
    } catch (error) {
      log.error('Error updating cart:', error);
      const message = error.response?.data?.error;
      if (message === 'TOKEN_EXPIRED' || message === 'INVALID_ID_TOKEN') {
        handleSessionExpired();
      }
    }
  }, [adminImpersonate, user, idToken, setCartId, handleSessionExpired]);

  const changeQuantity = useCallback(async (product, type) => {
    const currentQty = getQuantityFromCart(product._id, product.variantId);
    const minQty = product.minQuantity || 1;
    const maxQty = product.maxQuantity || Infinity;

    let newQty;
    if (type === 'increase') {
      if (currentQty >= maxQty) {
        showToast(`${t('maximumQuantityAllowed')} ${maxQty}`, 'warning');
        return;
      }
      newQty = currentQty + 1;
    } else {
      newQty = currentQty - 1;
    }

    try {
      const finalQty = newQty < minQty ? 0 : newQty;
      updateQuantity(product._id, product.merchantConfigId, product.variantId, finalQty);
      await updateQuantityOnServer(product, finalQty);
    } catch (err) {
      log.error(err);
    }
  }, [getQuantityFromCart, showToast, t, updateQuantity, updateQuantityOnServer]);

  return {
    addToCart,
    getMaxVariantFromCart,
    getVariantQuantitiesFromCart,
    changeQuantity,
    updateQuantityOnServer,
    toastMessage,
    toastType,
    showToast,
    cartItems,
  };
};

export default useCartActions;
